import { useEffect, useRef } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { useDispatch, useSelector } from "react-redux";
import {
  selectCurrentProject,
  selectDefaultProject,
  selectProjectList,
} from "../../store/slices/projects";
import {
  SESSION_ANONYMOUS,
  SESSION_AUTHENTICATED,
  selectSessionStatus,
} from "../../store/slices/user";
import { bootstrapProjects } from "../../store/thunks/bootstrapProjects";

/**
 * LF360-1970: "no project yet" is not "not signed in".
 *
 * This sent anybody with no project in the store to `/login`, and that is the
 * same mistake the session gate used to make: treating "not known yet" as an
 * answer. The projects arrive from a request, so on the way in there is always
 * a moment when the list is empty because nothing has come back — and landing
 * here in that moment threw a signed-in administrator out to the login screen,
 * where the session gate would let them back in, which would land them here
 * again.
 *
 * So an empty list means wait. A list that has arrived and holds no project the
 * user can use is a different thing entirely — it is an account with no project,
 * which the login screen cannot fix either, but at least the redirect is then
 * an answer rather than a race.
 *
 * That left a third case as neither: **a visitor who is not signed in**. Nobody
 * is going to ask for their projects, so the list stays empty for good and
 * "wait" never ends — `/` rendered nothing at all, on any browser, for anyone
 * arriving at the portal without a session. Which is how most people arrive.
 *
 * The session status is what tells the two apart, and it is an answer rather
 * than a guess: `useSession` asks the server once at start-up precisely because
 * an HttpOnly cookie is not something the page can look at. Unknown means the
 * question is still out; anonymous means it came back and there is no session.
 */
export const LegacyProjectRedirect = ({ targetPath }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const current = useSelector(selectCurrentProject);
  const def = useSelector(selectDefaultProject);
  const list = useSelector(selectProjectList);
  const session = useSelector(selectSessionStatus);
  const dispatch = useDispatch();
  // One request, however many times the effect runs.
  const asked = useRef(false);

  /**
   * Nothing on these routes fetches the project list.
   *
   * `bootstrapProjects` is dispatched by the layouts and by the login screen,
   * and none of them renders here: `/` sits above `PrivateRoutes`, and
   * `PrivateRoutes` itself only decides whether to render an outlet. So a
   * signed-in administrator opening `/` — a bookmark, a typed address, a
   * restored tab — waited for a list nobody had asked for, which is the same
   * blank page an anonymous visitor got, reached from the other side.
   *
   * Asking here rather than adding another caller: this component is the one
   * that cannot proceed without the answer.
   */
  useEffect(() => {
    if (session !== SESSION_AUTHENTICATED || asked.current) {
      return;
    }

    if (current || def || (list && list.length > 0)) {
      return;
    }

    asked.current = true;

    // A refusal must not read as "still loading" either. There is nothing to
    // show without projects, and the login screen asks again on its way past.
    Promise.resolve(dispatch(bootstrapProjects())).catch(() => {
      navigate("/login", { replace: true });
    });
  }, [session, current, def, list, dispatch, navigate]);

  useEffect(() => {
    const project = current || def;

    if (!project) {
      // A list that has arrived holding nothing usable is an answer, whoever
      // asked for it and whatever the session says.
      if (list && list.length > 0) {
        navigate("/login", { replace: true });

        return;
      }

      // No list — and for a visitor with no session there will not be one, so
      // this is an answer too rather than the start of a wait.
      if (session === SESSION_ANONYMOUS) {
        navigate("/login", { replace: true });

        return;
      }

      // Still being asked, or signed in with nothing back yet: render nothing,
      // and run again when the answer lands.
      return;
    }

    const code = (project.code || "").slice(0, 3);
    const path = targetPath || location.pathname;

    navigate(`/${code}${path}`, { replace: true });
  }, [current, def, list, session, location.pathname, targetPath, navigate]);

  return null;
};
